<template>
  <div>
    <div
      v-if="shouldShowUrgencyWarning"
      class="ai-urgency-card"
    >
      <div class="ai-urgency-card__title">
        <RobotIcon class="svg-correctionBlue-fill" />
        <span class="ms-1">AIが緊急の可能性がある内容を検知しました。</span>
      </div>
      <div
        v-if="assessmentBody"
        class="ai-urgency-card__body"
      >
        {{ assessmentBody }}
      </div>
    </div>
    <div
      v-if="showSuggestionNotice"
      class="ai-suggestion-notice"
    >
      内容を確認し、問題なければ「採用」を押してください。
    </div>
    <div
      id="assessment-complaint-row"
      class="l-assessmentRow"
    >
      <div class="assessment-row-label">
        主訴
      </div>
      <v-textarea
        id="input-complaint"
        :model-value="localComplaint"
        variant="outlined"
        hide-details
        name="complaint"
        density="compact"
        rows="1"
        max-rows="5"
        auto-grow
        :disabled="!answer.editable"
        required
        :class="'flex-grow-1 custom-compactInput'"
        no-resize
        @focus="onComplaintFocus"
        @blur="onComplaintBlur"
        @update:model-value="onComplaintModelUpdate"
      >
        <template #append-inner>
          <aiSuggestChip :status="complaintStatus" />
        </template>
      </v-textarea>
      <div class="assessment-mode-action">
        <button
          v-if="isComplaintSuggested"
          type="button"
          class="adopt-button"
          @click="onComplaintConfirm"
        >
          <v-icon size="14">
            mdi-check
          </v-icon>
          <span class="ms-1">採用</span>
        </button>
      </div>
    </div>
    <div class="mt-2 mb-5 complaint-hint">
      <div
        v-if="answer.editable && isComplaintFocused"
        style="color: #FB0606;"
      >
        【入力時のお願い】自治体・企業への共有や、相談事例として公開することがあります。<br>
        ※非医療者にも伝わる言葉で入力してください。<br>
        ※個人が特定できる情報（年齢・妊娠週数など）は入力しないでください。
      </div>
    </div>

    <div
      id="assessment-diagnosis-row"
      class="l-assessmentRow mt-3"
    >
      <div class="assessment-row-label">
        暫定診断
      </div>
      <div class="flex-grow-1">
        <midwife-diagnosis
          v-if="departmentType === 'midwife'"
          :karte="localDiagnosisKarte"
          :active="answer.editable"
          :use-anytime-ui="true"
          :status="diagnosisStatus"
        />
        <diagnosis
          v-else
          :karte="localDiagnosisKarte"
          :department-data="departmentType"
          :active="answer.editable"
          :use-anytime-ui="true"
          :status="diagnosisStatus"
        />
      </div>
      <div class="assessment-mode-action">
        <button
          v-if="isDiagnosisSuggested"
          type="button"
          class="adopt-button"
          @click="onDiagnosisConfirm"
        >
          <v-icon size="14">
            mdi-check
          </v-icon>
          <span class="ms-1">採用</span>
        </button>
      </div>
    </div>

    <div class="d-flex align-center justify-space-between flex-wrap mb-3 diagnosis-candidates-row">
      <div
        v-if="answer.editable && otherDiagnosisCandidates.length > 0 && !isDiagnosisDone"
        class="mt-1"
      >
        <span style="font-size: 12px; color: #8a8a8a;">他の候補：</span>
        <v-chip
          v-for="candidate in otherDiagnosisCandidates"
          :key="candidate.id"
          size="small"
          class="ml-1 diagnosis-candidate-chip"
          @click="onDiagnosisCandidateClick(candidate)"
        >
          {{ candidate.name }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Diagnosis from "@/vue/components/shared/Diagnosis.vue";
import MidwifeDiagnosis from "@/vue/components/shared/MidwifeDiagnosis.vue";
import aiSuggestChip from "@/vue/components/shared/anytime_consultation_form/aiSuggestChip.vue";
import RobotIcon from "@/vue/components/images/robotIcon.vue";
import {
  useAssessmentInputs,
  type AssessmentAnswer,
  type ConsultationAssessmentDraft,
  type DiagnosisValue,
} from "@/vue/features/anytime-consultation-assessment";

const props = withDefaults(
  defineProps<{
    answer: AssessmentAnswer;
    departmentType?: string;
    consultationAssessmentDraft?: ConsultationAssessmentDraft | null;
    preprocessingStatus?: string;
  }>(),
  {
    departmentType: "",
    consultationAssessmentDraft: null,
    preprocessingStatus: "idle",
  }
);

// 完了（confirmed/revised/saved）になったときだけ store answer へコミットする。
const emit = defineEmits<{
  "update:complaint": [value: string];
  "update:diagnosis": [value: DiagnosisValue];
}>();

// ステータス管理の複雑さは features/anytime-consultation-assessment に閉じ込める。
const {
  isComplaintFocused,
  localComplaint,
  localDiagnosisKarte,
  complaintStatus,
  diagnosisStatus,
  isComplaintSuggested,
  isDiagnosisSuggested,
  isDiagnosisDone,
  showSuggestionNotice,
  assessmentBody,
  shouldShowUrgencyWarning,
  otherDiagnosisCandidates,
  onComplaintFocus,
  onComplaintBlur,
  onComplaintModelUpdate,
  onComplaintConfirm,
  onDiagnosisConfirm,
  onDiagnosisCandidateClick,
} = useAssessmentInputs(props, emit);
</script>

<style lang="scss" scoped>
.l-assessmentRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .assessment-row-label {
    width: 60px;
    min-width: 60px;
    font-size: 13px;
    color: #333;
    line-height: 1.2;
    margin-right: 8px;
  }

  @media (max-width: 600px) {
    .assessment-row-label {
      width: 100%;
      min-width: 100%;
      margin-right: 0;
      margin-bottom: 6px;
    }
  }

  :deep(.v-input.v-text-field:not(.v-input--disabled) .v-field) {
    background-color: #fff !important;
  }

  :deep(.v-input.v-input--disabled .v-field) {
    background-color: transparent !important;
    opacity: 1 !important;
  }

  &.assessment-row--error {
    .assessment-row-label {
      color: #dc2626;
    }

    :deep(.v-field__outline) {
      --v-field-border-opacity: 1;
      color: #dc2626 !important;
    }

    .adopt-button {
      border-color: #dc2626;
      box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.2);
    }
  }
}

// 主訴のtextareaはauto-growで高さが変わるため、ラベル・採用ボタンを上端に揃える。
#assessment-complaint-row {
  align-items: flex-start;

  .assessment-row-label {
    margin-top: 12px;
  }

  .assessment-mode-action {
    margin-top: 8px;
  }

  @media (max-width: 600px) {
    .assessment-row-label {
      margin-top: 0;
    }
  }
}

.diagnosis-candidates-row {
  margin-left: 68px;

  @media (max-width: 600px) {
    margin-left: 0;
  }
}

.complaint-hint {
  margin-left: 68px;

  @media (max-width: 600px) {
    margin-left: 0;
  }
}

.diagnosis-candidate-chip {
  color: rgb(var(--v-theme-correctionBlue)) !important;
}

.custom-compactInput {
  :deep(.v-input__control) {
    min-height: 40px;
  }
}

.assessment-mode-action {
  margin-left: 8px;
}

.adopt-button {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  color: #ffffff;
  background-color: #1564bd;
  border: 1px solid #0f4f98;
  box-shadow: 0 1px 2px rgba(15, 79, 152, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #0f4f98;
  }
}

.ai-suggestion-note {
  color: rgb(var(--v-theme-correctionBlue));
  font-size: 12px;
  line-height: 1.2;
  margin-bottom: 6px;

  &--muted {
    color: #64748b;
  }
}

.ai-urgency-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;

  &__title {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 700;
    color: rgb(var(--v-theme-correctionBlue));
  }

  &__body {
    margin-top: 6px;
    font-size: 13px;
    line-height: 1.6;
    color: #333;
    white-space: pre-wrap;
  }
}

.ai-suggestion-notice {
  font-size: 13px;
  color: rgb(var(--v-theme-correctionBlue));
  margin-bottom: 12px;
}

.assessment-urgency-note {
  color: #333;
  border-left: 2px solid rgba(21, 100, 189, 0.5);
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 8px;
}

.svg-correctionBlue-fill {
  fill: #1564bd;
}
</style>
